import React from 'react';
import { Phone, Mail, MapPin, Instagram, Clock, Music2 } from 'lucide-react';
import { businessInfo } from '../mock';

const Footer = () => {
  const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer id="contact" className="bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600 rounded-full blur-3xl opacity-10"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_ad632560-d0a8-4e29-a8e3-0fdc0eb21a29/artifacts/erjp1y5v_594DED26-5434-47CF-8EE8-EA9BF153DAD6.PNG"
                alt="ProClean"
                className="h-14 w-auto"
              />
            </div>
            <p className="text-slate-300 text-lg font-medium mb-2">{businessInfo.tagline}</p>
            <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-md">
              Sydney's trusted mobile detailing service. Premium care for your vehicle, delivered to your door.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href={businessInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2.5 rounded-xl transition-colors"
              >
                <Instagram className="w-5 h-5 text-pink-400" />
                <span className="text-white font-semibold text-sm">{businessInfo.instagram}</span>
              </a>
              <a
                href={businessInfo.tiktokUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2.5 rounded-xl transition-colors"
              >
                <Music2 className="w-5 h-5 text-cyan-300" />
                <span className="text-white font-semibold text-sm">TikTok {businessInfo.tiktok}</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-3">
            <h3 className="font-bold text-base mb-4 text-white">Explore</h3>
            <ul className="space-y-3">
              {['about', 'services', 'gallery', 'testimonials', 'booking'].map((id) => (
                <li key={id}>
                  {id === 'gallery' ? (
                    <a
                      href="/gallery"
                      data-testid="footer-link-gallery"
                      className="text-slate-400 hover:text-white transition-colors text-sm"
                    >
                      Gallery
                    </a>
                  ) : (
                    <button
                      data-testid={`footer-link-${id}`}
                      onClick={() => scrollToSection(id)}
                      className="text-slate-400 hover:text-white transition-colors text-sm capitalize"
                    >
                      {id === 'booking' ? 'Book Now' : id}
                    </button>
                  )}
                </li>
              ))}
              <li>
                <a
                  href="/special-services"
                  data-testid="footer-link-special-services"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                >
                  Special Services
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-4">
            <h3 className="font-bold text-base mb-4 text-white">Get In Touch</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${businessInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start gap-3 text-slate-300 hover:text-white transition-colors group"
                >
                  <div className="w-9 h-9 bg-white/5 group-hover:bg-blue-500 rounded-lg flex items-center justify-center transition-colors flex-shrink-0">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Call</div>
                    <div className="font-semibold">{businessInfo.phone}</div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${businessInfo.email}`}
                  className="flex items-start gap-3 text-slate-300 hover:text-white transition-colors group"
                >
                  <div className="w-9 h-9 bg-white/5 group-hover:bg-blue-500 rounded-lg flex items-center justify-center transition-colors flex-shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Email</div>
                    <div className="font-semibold text-sm break-all">{businessInfo.email}</div>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-300">
                <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Area</div>
                  <div className="font-semibold">{businessInfo.serviceArea}</div>
                </div>
              </li>
              <li className="flex items-start gap-3 text-slate-300">
                <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Hours</div>
                  <div className="font-semibold text-sm">Mon–Sat, 8am–7pm</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} ProClean Mobile Detailing. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Professional care, brought to you.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
